<template>
	<CDialog
		ref="dialogRef"
		:custom-class="dialogClass"
		data-testid="task-lightbox"
		visible
		:title="dialogTitle"
		:is-loading="isLoading"
		:max-width="formWidth"
		destroy-on-close
		header-is-sticky
		:show-confirm="entryHasChanges"
		:confirm-options="confirmOptions"
		:close-on-route-change="false"
		:resources="resources"
		@close="onLightboxClose"
	>
		<div id="cognito-form" class="form-lightbox app-styled-form" :inert="!formIsReady">
			<div ref="mountFormHere"></div>
		</div>
	</CDialog>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { defineComponent, ref, onMounted, onBeforeUnmount, nextTick, computed, inject } from 'vue';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import notify from 'src/utilities/notify';
	import type { AdminFormApi } from './utils/admin-api';
	import type { AfterSubmitEvent, CreateToastMessageEvent } from '../../../../client/src/framework/public/events';
	import type { FormLightboxFormHandle } from '../../../../client/src/framework/admin/form-lightbox-form-handle';
	import type { EmbedContext } from '../../../../client/src/framework/public/form-handle';
	import { localizationResourcesKey } from 'src/utilities/injection-keys';

	export default defineComponent({
		name: 'CFormLightbox',
		components: { CDialog },
		props: {
			adminApi: { type: Object as PropType<AdminFormApi>, required: true },
			formId: { type: String, required: true },
			viewToken: { type: String, default: null },
			viewId: { type: String, default: null },
			taskRoleId: { type: Number, default: undefined },
			entryKey: { type: Object, default: () => ({}) },
			entryJson: { type: Object, default: () => ({}) },
			dialogTitle: { type: String, required: true },
			inlineActionName: { type: String, default: null },
			showLocalizedError: { type: Boolean, default: false },
			clientRole: { type: String as PropType<ClientRole>, required: true },
			userEmail: { type: String, default: null },
			embedContext: { type: String as PropType<EmbedContext>, required: true }
		},
		emits: ['close-dialog', 'submit', 'save', 'before-submit'],
		setup(props, { emit }) {
			const resources = inject(localizationResourcesKey);

			const isEntryMounted = ref(false);
			const entryHasChanges = ref(false);
			const isLoading = ref(true);

			let entryAdapter: FormLightboxFormHandle = null;
			const mountEntry = async () => {
				await entryAdapter.changeView(props.viewId, props.viewToken, false);
				await entryAdapter.setExistingEntry(props.entryKey, props.taskRoleId);

				isEntryMounted.value = true;
				entryAdapter.addChangesDetected(() => {
					entryHasChanges.value = true;
				});
			};

			const focusXButton = () => {
				nextTick(() => {
					(document.querySelector('.form-lightbox-dialog .dialog__close-button') as HTMLElement)?.focus();
				});
			};

			const mountEntryJson = async () => {
				await entryAdapter.setProfile(props.entryJson);

				isEntryMounted.value = true;
				entryAdapter.addChangesDetected(() => {
					entryHasChanges.value = true;
				});
			};

			const beforeSubmit = () => {
				emit('before-submit');
			};

			const afterSubmit = (value: AfterSubmitEvent) => {
				entryHasChanges.value = false;
				emit('submit', value.data.convertedMessage);
				focusXButton();
			};

			let firstSave = true;
			const afterSave = () => {
				entryHasChanges.value = false;

				if (props.clientRole === ClientRole.Guest && props.userEmail && firstSave)
					entryAdapter.emailResumeLink(props.userEmail);

				firstSave = false;
				emit('save');
			};

			const createToastMessage = (event : CreateToastMessageEvent) => {
				notify(event.data.toastMessage, event.data.toastType);
			};

			const headerHeight = ref(0);
			const dialogRef = ref<InstanceType<typeof CDialog> | null>(null);
			let resizeObserver: ResizeObserver | null = null;

			const updateHeaderHeight = () => {
				const dialog = dialogRef.value.$refs.dialog as Vue & { $el: Element };
				const dialogHeader = dialog.$el.querySelector('.el-dialog__header') as HTMLElement;
				const dialogWrapper = dialog.$el.closest('.el-dialog__wrapper') as HTMLElement;

				if (window.ResizeObserver && dialogHeader) {
					if (resizeObserver) {
						resizeObserver.disconnect();
					}

					resizeObserver = new ResizeObserver(() => {
						const newHeight = dialogHeader.offsetHeight;
						if (newHeight !== headerHeight.value && newHeight > 0) {
							headerHeight.value = newHeight;
							if (dialogWrapper) {
								dialogWrapper.style.setProperty('--dialog-header-height', `${newHeight}px`);
							}
						}
					});
					resizeObserver.observe(dialogHeader);
				}
			};

			const formWidth = ref('');
			const mountFormHere = ref<HTMLElement | null>(null);
			const mountForm = async () => {
				// Ensure that we set the formWidth
				const formDef = await props.adminApi.getFormDefinition(props.formId, props.embedContext);
				formWidth.value = formDef.theme.style.textContent.match(/--form__width:\s*([^;]+);/)[1].trim();

				entryAdapter = await props.adminApi.mount(props.formId, mountFormHere.value as HTMLElement, props.embedContext) as FormLightboxFormHandle;
				entryAdapter.on('beforeSubmit', beforeSubmit);
				entryAdapter.on('afterSubmit', afterSubmit);
				entryAdapter.on('afterSave', afterSave);
				entryAdapter.on('createToastMessage', createToastMessage);
				if (props.entryKey)
					await mountEntry();
				else if (props.entryJson)
					await mountEntryJson();

				if (props.inlineActionName)
					nextTick(async () => {
						await entryAdapter.performAction(props.inlineActionName);
						entryHasChanges.value = false;
					});

				isLoading.value = false;
				focusXButton();

				if (props.showLocalizedError)
					notify(entryAdapter.getLocalizedError('concurrent-conflict-toast-message'), 'error');
			};

			onMounted(() => {
				nextTick(() => {
					document.body.classList.add('app-styled-form');
					mountForm();
					updateHeaderHeight();
				});
			});

			onBeforeUnmount(() => {
				if (resizeObserver) {
					resizeObserver.disconnect();
				}
				document.body.classList.remove('app-styled-form');
			});

			const onLightboxClose = async () => {
				emit('close-dialog', false);
			};

			const confirmOptions = {
				showClose: true,
				title: resources.value.UnsavedChanges,
				message: resources.value.AreYouSureYouWantToDiscardYourChanges,
				confirmButtonText: resources.value.DiscardChanges,
				cancelButtonText: resources.value.KeepEditing,
				customClass: 'notification notification--confirmation-dialog',
				confirmButtonClass: 'button button--secondary sz-3',
				cancelButtonClass: 'button button--primary sz-3'
			};

			const formIsReady = computed(() => {
				return !isLoading.value && formWidth.value;
			});

			const dialogClass = computed(() => {
				const baseClass = 'dialog--wide form-lightbox-dialog';

				return formIsReady.value ? baseClass + ' form-lightbox-dialog--ready' : baseClass;
			});

			return {
				resources,
				mountFormHere,
				onLightboxClose,
				confirmOptions,
				isEntryMounted,
				entryHasChanges,
				formWidth,
				isLoading,
				formIsReady,
				dialogClass,
				dialogRef
			};
		}
	});
</script>

<style lang="scss" src="@cognitoforms/styles/app/cognito-form.scss"></style>

<style lang="scss">
/* stylelint-disable selector-max-specificity */
#cognito-form.form-lightbox {

	.cog-header {
		display: none;
	}

	.cog-form--dark-background {

		.cog-col.is-error::before {
			background: none;
			outline: none;
			opacity: 1;
		}

		.cog-col.is-error::after {
			background: none;
			outline: none;
			opacity: 1;
		}
	}

	.el-input__suffix {
		padding-right: 0;
		padding-left: 0;
	}

	.cog-page-progress {
		position: sticky !important;
		top: var(--dialog-header-height, 76px) !important;
		z-index: $z1 !important;
		background: var(--bg-primary) !important;
	}
}

// Fix for textarea height calculation when dialog reopens
.form-lightbox-dialog {

	&:not(.form-lightbox-dialog--ready) .dialog__content-container {
		// Force content to be in DOM but stay hidden
		display: block !important;
		height: 0;
		overflow: hidden;
		visibility: hidden;
	}
}
</style>
